/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import './TermsOfUse.css';

// Product Terms of Use (Doc No. BLITZ 000004) — the terms governing use of the
// Bltz AI software/agent/extension, linked from the installer / setup page.
// Distinct from /terms (Website Terms of Use).
const SoftwareTerms = () => {
  return (
    <div className="terms-page">
      <div className="terms-container">
        <h1>Bltz AI Product Terms of Use</h1>
        <p className="terms-effective">Version: 03-01-2026 &middot; Effective Date: March 1, 2026</p>

        <section>
          <p>
            These Product Terms of Use ("Product Terms") govern Customer's and its Authorized Users' access
            to and use of the Bltz AI platform, software, APIs, browser extensions, endpoint components,
            proxy agents, administrative portal, Documentation, and related product features made available
            by Bltz AI, Inc. ("Company") as part of the Services.
          </p>
          <p>
            These Product Terms are incorporated into and form part of the applicable Master Services
            Agreement, Order Form, Statement of Work, Data Processing Addendum, or other written agreement
            between Company and Customer governing Customer's use of the Services ("Customer Agreement").
            Capitalized terms not defined in these Product Terms have the meanings given in the Customer
            Agreement.
          </p>
          <p>
            These Product Terms are intended to supplement, not replace, the Customer Agreement. In the
            event of any conflict between these Product Terms and the Customer Agreement, the Customer
            Agreement will control.
          </p>
          <p>
            These Product Terms do not govern general public use of Company's website, which is governed by
            Company's Website Terms of Use.
          </p>
        </section>

        <section>
          <h2>1. Access to the Services</h2>
          <p>
            Subject to these Product Terms and the Customer Agreement, Company grants Customer and its
            Authorized Users a limited, non-exclusive, non-transferable, non-sublicensable right to access
            and use the Services solely for Customer's internal business purposes during the applicable
            subscription, trial, proof-of-concept, design partner, or evaluation period.
          </p>
          <p>
            Customer may permit Authorized Users to access and use the Services, provided that Customer
            remains responsible for all activity by Authorized Users and for their compliance with these
            Product Terms, the Customer Agreement, Documentation, and applicable law.
          </p>
          <p>
            Customer is responsible for maintaining the confidentiality and security of all accounts,
            credentials, access tokens, API keys, endpoint agents, browser extensions, administrative
            access, and integrations used in connection with the Services.
          </p>
          <p>
            Customer must promptly notify Company of any suspected unauthorized access, compromise, misuse,
            or security issue involving the Services.
          </p>
        </section>

        <section>
          <h2>2. Customer Responsibilities</h2>
          <p>
            Customer is responsible for determining whether and how to deploy, configure, and use the
            Services within Customer's environment.
          </p>
          <p>
            Customer is responsible for configuring policies, monitoring scope, enforcement actions,
            retention settings, redaction settings, integrations, users, endpoints, applications, browsers,
            administrative roles, and other settings.
          </p>
          <p>
            Customer is responsible for obtaining and maintaining all rights, permissions, consents,
            notices, approvals, and legal bases required for Customer's use of the Services, including any
            monitoring, inspection, logging, routing, policy enforcement, analysis, or processing of user
            activity, endpoint activity, browser activity, AI prompts, AI responses, files, code, content,
            telemetry, metadata, or Personal Data.
          </p>
          <p>
            Customer is responsible for ensuring that its use of the Services complies with applicable laws,
            employment rules, workplace monitoring requirements, privacy requirements, internal policies,
            collective bargaining obligations, contractual obligations, and third-party service terms.
          </p>
          <p>
            Customer is responsible for maintaining Customer systems, networks, devices, endpoints,
            browsers, identity providers, AI tools, cloud environments, and third-party integrations.
          </p>
          <p>
            Customer is responsible for reviewing and validating outputs, reports, classifications, alerts,
            recommendations, detections, policy actions, remediation suggestions, and other results generated
            by the Services before relying on them or taking action.
          </p>
          <p>
            Company will provide the Services in a manner consistent with the Customer Agreement,
            Documentation, and applicable industry standards.
          </p>
        </section>

        <section>
          <h2>3. Deployment and Endpoint Components</h2>
          <p>
            The Services may include browser extensions, native applications, endpoint agents, proxy
            components, API integrations, gateways, connectors, or other software components deployed in
            Customer-managed environments.
          </p>
          <p>
            Customer is responsible for deployment, configuration, permissions, user communications,
            endpoint management, change management, compatibility testing, and compliance with applicable
            laws and internal policies related to such components.
          </p>
          <p>
            Customer may not deploy the Services in a manner that exceeds the applicable Order Form, licensed
            scope, authorized use case, Documentation, or written approval from Company.
          </p>
          <p>
            Customer must not modify, tamper with, disable, bypass, reverse engineer, or interfere with any
            endpoint, browser, proxy, API, gateway, monitoring, security, or enforcement component of the
            Services.
          </p>
        </section>

        <section>
          <h2>4. Acceptable Use</h2>
          <p>
            Customer and Authorized Users may not use the Services to violate any applicable law, regulation,
            third-party right, or contractual obligation.
          </p>
          <p>
            Customer and Authorized Users may not use the Services to transmit, upload, process, store,
            route, generate, or distribute unlawful, harmful, infringing, defamatory, obscene, abusive,
            deceptive, fraudulent, or otherwise inappropriate content.
          </p>
          <p>
            Customer and Authorized Users may not use the Services to develop, facilitate, test, deploy, or
            enable malware, ransomware, spyware, phishing, credential theft, unauthorized surveillance,
            evasion, exploit code, or other harmful activity, except to the extent expressly authorized by
            Company in writing for legitimate security testing or evaluation.
          </p>
          <p>
            Customer and Authorized Users may not attack, probe, scan, overload, disrupt, disable, interfere
            with, or compromise the Services, Company systems, other customers' data, or third-party systems,
            except as expressly authorized by Company in writing.
          </p>
          <p>
            Customer and Authorized Users may not attempt to gain unauthorized access to the Services,
            Company systems, other customers' data, or third-party systems.
          </p>
          <p>
            Customer and Authorized Users may not bypass, disable, interfere with, or circumvent security,
            policy, monitoring, access control, usage, rate-limit, licensing, or enforcement mechanisms.
          </p>
          <p>
            Customer and Authorized Users may not use the Services to build, train, improve, benchmark,
            evaluate, or compare a competing product or service without Company's prior written consent.
          </p>
          <p>
            Customer and Authorized Users may not use the Services in a manner that could subject Company,
            Customer, users, or third parties to legal, security, operational, reputational, or regulatory
            risk.
          </p>
          <p>Customer and Authorized Users may not permit any third party to do any of the foregoing.</p>
        </section>

        <section>
          <h2>5. Product Restrictions</h2>
          <p>Customer may not, and may not permit any Authorized User or third party to:</p>
          <p>(a) copy, modify, translate, adapt, or create derivative works of the Services;</p>
          <p>
            (b) reverse engineer, decompile, disassemble, or otherwise attempt to derive source code, object
            code, models, algorithms, detection logic, prompts, workflows, rules, policies, templates,
            architecture, or non-public technical information from the Services;
          </p>
          <p>
            (c) sell, resell, sublicense, distribute, rent, lease, timeshare, outsource, or provide access to
            the Services to any third party except as expressly permitted in the Customer Agreement;
          </p>
          <p>(d) remove, obscure, or alter proprietary notices;</p>
          <p>
            (e) use the Services for the benefit of any third party except Customer's Affiliates or
            Authorized Users as permitted under the Customer Agreement;
          </p>
          <p>(f) access the Services to monitor availability, performance, or functionality for competitive purposes;</p>
          <p>
            (g) perform security testing, vulnerability scanning, penetration testing, load testing,
            benchmarking, or technical assessment of the Services without Company's prior written approval;
          </p>
          <p>(h) interfere with or disrupt the integrity, security, availability, or performance of the Services;</p>
          <p>
            (i) use the Services outside the licensed scope, usage limits, subscription tier, evaluation
            scope, or Order Form; or
          </p>
          <p>(j) use the Services in violation of Documentation or written instructions provided by Company.</p>
        </section>

        <section>
          <h2>6. AI-Specific Terms</h2>
          <p>
            The Services may use artificial intelligence, machine learning, automation, heuristics, rules,
            classifiers, analytics, evaluations, and other automated methods to provide AI security,
            governance, visibility, risk detection, policy enforcement, runtime protection, reporting, and
            remediation-support functionality.
          </p>
          <p>
            Customer acknowledges that AI-enabled features may generate outputs, scores, classifications,
            alerts, recommendations, reports, remediation suggestions, policy actions, or other results that
            may be incomplete, inaccurate, or require human review.
          </p>
          <p>Customer is responsible for reviewing and validating outputs before relying on them or taking action.</p>
          <p>
            The Services are designed to support security, governance, compliance, and operational workflows.
            They are not a substitute for Customer's independent legal, compliance, security, privacy,
            employment, HR, or risk-management judgment.
          </p>
          <p>
            Company does not warrant that the Services will identify, prevent, detect, block, or remediate
            all threats, vulnerabilities, jailbreaks, prompt injections, data exposures, policy violations,
            compliance issues, unauthorized AI use, or Security Incidents.
          </p>
          <p>
            Customer may not use the Services to make automated decisions that produce legal or similarly
            significant effects on individuals without appropriate human review, lawful basis, notice,
            governance, and safeguards.
          </p>
        </section>

        <section>
          <h2>7. Customer Data</h2>
          <p>As between Customer and Company, Customer retains all right, title, and interest in Customer Data.</p>
          <p>
            Company may access, use, process, transmit, store, analyze, and otherwise handle Customer Data
            only as permitted under the Customer Agreement, the DPA, these Product Terms, and applicable law.
          </p>
          <p>Company will not sell Customer Data or Personal Data.</p>
          <p>
            Company will not use Customer Data to train or improve general-purpose AI models, foundation
            models, or third-party AI models except as expressly authorized by Customer in writing.
          </p>
          <p>
            Company may compile and use aggregated or anonymized data as described in the Customer Agreement
            and DPA, provided that such data does not identify Customer or any individual and is not
            reasonably capable of being associated with Customer or any individual.
          </p>
        </section>

        <section>
          <h2>8. Privacy and Data Protection</h2>
          <p>
            Company's Processing of Personal Data is governed by the Customer Agreement, including the DPA
            where applicable.
          </p>
          <p>
            Customer is responsible for providing all notices, obtaining all consents, and establishing all
            lawful bases required for the collection, monitoring, inspection, transmission, storage, analysis,
            and Processing of Personal Data through the Services.
          </p>
          <p>
            Depending on Customer's configuration, the Services may process user identifiers, email
            addresses, account information, endpoint metadata, browser metadata, AI usage activity,
            application activity, policy detections, prompts, responses, files, code, content, logs,
            telemetry, administrative actions, and other data submitted to or collected by the Services.
          </p>
          <p>
            Customer is responsible for determining whether to submit sensitive personal data, regulated
            data, or highly confidential data to the Services and for configuring the Services appropriately,
            including use of redaction, policy enforcement, retention, access control, and other available
            controls.
          </p>
        </section>

        <section>
          <h2>9. Security</h2>
          <p>
            Company maintains administrative, technical, and organizational safeguards designed to protect
            the confidentiality, integrity, and availability of the Services and Customer Data, as further
            described in the Customer Agreement and applicable security documentation.
          </p>
          <p>
            Customer is responsible for securely configuring and using the Services, including identity
            settings, administrative permissions, endpoint deployment, browser permissions, policy
            configurations, API keys, integrations, and Authorized User access.
          </p>
          <p>
            Customer must promptly notify Company of any suspected vulnerability, unauthorized access,
            misuse, or security issue related to the Services.
          </p>
          <p>
            Customer may not conduct vulnerability scanning, penetration testing, security testing, technical
            assessment, benchmarking, or load testing of the Services without Company's prior written
            approval.
          </p>
        </section>

        <section>
          <h2>10. Third-Party Services and AI Providers</h2>
          <p>
            The Services may interoperate with or depend on third-party products, services, applications, AI
            providers, cloud platforms, APIs, identity providers, browsers, app stores, endpoint management
            systems, or customer-selected integrations ("Third-Party Services").
          </p>
          <p>
            Third-Party Services are governed by their own terms, policies, security programs, availability
            commitments, and data practices. Company does not control and is not responsible for Third-Party
            Services.
          </p>
          <p>
            Where Customer configures or uses the Services to route, inspect, monitor, block, modify,
            analyze, or interact with Third-Party Services, Customer is responsible for ensuring that such
            use is permitted under applicable law and applicable third-party terms.
          </p>
          <p>
            Company is not responsible for the availability, security, performance, outputs, decisions, acts,
            omissions, or data practices of Third-Party Services, including third-party AI providers and AI
            model outputs, except to the extent expressly stated in the Customer Agreement.
          </p>
        </section>

        <section>
          <h2>11. Beta, Trial, Design Partner, Evaluation, and Preview Use</h2>
          <p>
            Company may provide beta, trial, design partner, proof-of-concept, preview, experimental, or
            evaluation features or services ("Evaluation Services").
          </p>
          <p>
            Evaluation Services are provided for evaluation only, may be modified or discontinued at any time,
            and may not be as reliable, secure, available, complete, or fully supported as generally available
            production Services.
          </p>
          <p>
            Evaluation Services are provided "AS IS" and without warranties, indemnities, service-level
            commitments, or support commitments, except to the extent expressly stated in the Customer
            Agreement.
          </p>
          <p>
            Customer may use Evaluation Services only for the authorized evaluation purpose, duration, users,
            systems, applications, and scope approved by Company.
          </p>
        </section>

        <section>
          <h2>12. Suspension and Enforcement</h2>
          <p>Company may suspend, limit, or terminate access to the Services as permitted under the Customer Agreement.</p>
          <p>Company may also suspend, limit, or terminate access to the Services if Company reasonably determines that:</p>
          <p>
            (a) Customer or an Authorized User has violated these Product Terms, the Customer Agreement,
            Documentation, or applicable law;
          </p>
          <p>(b) use of the Services creates a security, legal, operational, reputational, or availability risk;</p>
          <p>
            (c) suspension is necessary to protect the Services, Company, Customer, users, other customers, or
            third parties;
          </p>
          <p>
            (d) Customer's account, systems, credentials, endpoints, integrations, or use of the Services are
            subject to unauthorized access, compromise, or misuse;
          </p>
          <p>(e) Customer exceeds licensed usage limits or uses the Services outside the authorized scope;</p>
          <p>(f) Customer fails to pay undisputed amounts when due, where applicable; or</p>
          <p>(g) suspension is required by law, government request, or third-party provider requirement.</p>
          <p>
            Where practicable, Company will provide notice and an opportunity to cure before suspension,
            unless immediate action is reasonably necessary.
          </p>
        </section>

        <section>
          <h2>13. Proprietary Rights</h2>
          <p>
            Company and its licensors retain all right, title, and interest in and to the Services,
            Documentation, software, APIs, agents, extensions, user interfaces, workflows, models,
            algorithms, detections, policies, rules, templates, reports, dashboards, product designs,
            improvements, and all related Intellectual Property Rights.
          </p>
          <p>
            Except for the limited right to use the Services under these Product Terms or the Customer
            Agreement, no rights are granted by implication, estoppel, or otherwise.
          </p>
          <p>
            Customer may provide Feedback regarding the Services. Company may use Feedback without restriction
            or obligation to Customer as described in the Customer Agreement.
          </p>
        </section>

        <section>
          <h2>14. Confidentiality</h2>
          <p>
            Non-public product, technical, security, commercial, pricing, roadmap, architecture, workflow,
            detection, evaluation, policy, and other proprietary information disclosed by Company is Company
            Confidential Information.
          </p>
          <p>
            Customer may not disclose Company Confidential Information except as permitted under the Customer
            Agreement or with Company's prior written consent.
          </p>
          <p>
            Customer may not publish, distribute, benchmark, reverse engineer, or use Company Confidential
            Information to build, improve, compare, or evaluate a competing product or service.
          </p>
        </section>

        <section>
          <h2>15. Support and Maintenance</h2>
          <p>
            Company may provide support and maintenance as described in the Customer Agreement, Order Form,
            Documentation, or other written support terms made available by Company.
          </p>
          <p>
            Company may update, modify, enhance, or discontinue features from time to time, provided that
            Company will not materially reduce the core functionality of paid production Services during the
            then-current subscription term unless otherwise permitted under the Customer Agreement.
          </p>
          <p>
            Company may perform maintenance, updates, patches, or changes to preserve or improve security,
            reliability, availability, performance, compliance, or functionality of the Services.
          </p>
        </section>

        <section>
          <h2>16. Disclaimers</h2>
          <p>The Services are subject to the warranty terms and disclaimers in the Customer Agreement.</p>
          <p>
            Without limiting the Customer Agreement, Company does not warrant that the Services will detect,
            prevent, block, identify, or remediate all security threats, vulnerabilities, jailbreaks, prompt
            injections, policy violations, data exposures, compliance issues, unauthorized AI use, or Security
            Incidents.
          </p>
          <p>
            Company does not warrant that AI-enabled outputs, scores, classifications, alerts,
            recommendations, reports, remediation suggestions, policy actions, or other results will be
            complete, accurate, error-free, or suitable for any particular purpose without Customer review and
            validation.
          </p>
          <p>
            If no Customer Agreement applies, the Services are provided "AS IS" and "AS AVAILABLE" to the
            maximum extent permitted by law.
          </p>
        </section>

        <section>
          <h2>17. Limitation of Liability</h2>
          <p>
            The limitation of liability provisions in the Customer Agreement govern all claims arising out of
            or relating to the Services, these Product Terms, and Customer's or Authorized Users' use of the
            Services.
          </p>
          <p>
            Nothing in these Product Terms expands Company's liability beyond the liability limits set forth
            in the Customer Agreement.
          </p>
          <p>
            If no Customer Agreement applies, to the maximum extent permitted by law, Company will not be
            liable for indirect, incidental, special, consequential, exemplary, punitive, or similar damages,
            including lost profits, lost revenue, loss of goodwill, business interruption, loss of data, or
            cost of substitute services.
          </p>
        </section>

        <section>
          <h2>18. Indemnity</h2>
          <p>
            The indemnity provisions in the Customer Agreement govern indemnification obligations between
            Company and Customer.
          </p>
          <p>
            Nothing in these Product Terms expands Company's indemnification obligations beyond those
            expressly stated in the Customer Agreement.
          </p>
          <p>
            To the extent no Customer Agreement applies, Customer will defend, indemnify, and hold harmless
            Company and its affiliates, officers, directors, employees, contractors, agents, licensors, and
            service providers from and against any claims, damages, liabilities, losses, costs, and expenses,
            including reasonable attorneys' fees, arising out of or related to:
          </p>
          <p>(a) Customer Data;</p>
          <p>
            (b) Customer's or Authorized Users' use of the Services in violation of these Product Terms,
            Documentation, or applicable law;
          </p>
          <p>(c) Customer's systems, networks, applications, endpoints, AI tools, integrations, or Third-Party Services;</p>
          <p>(d) Customer's failure to provide required notices, obtain required consents, or establish required lawful bases;</p>
          <p>(e) Customer's deployment, monitoring, inspection, logging, routing, blocking, policy enforcement, or Processing activities; or</p>
          <p>(f) Customer's violation of third-party rights.</p>
        </section>

        <section>
          <h2>19. Updates to Product Terms</h2>
          <p>
            Company may update these Product Terms from time to time by providing notice, including updated
            terms with Customer documentation, or otherwise making updated Product Terms available.
          </p>
          <p>
            Updates to these Product Terms will not materially reduce Customer's rights or materially increase
            Customer's obligations during the then-current subscription term unless otherwise agreed by the
            Parties.
          </p>
          <p>
            Customer's continued use of the Services after the effective date of updated Product Terms
            constitutes acceptance of the updated Product Terms to the extent permitted by applicable law and
            the Customer Agreement.
          </p>
        </section>

        <section>
          <h2>20. Termination</h2>
          <p>
            Customer may stop using the Services at any time, subject to any payment, term, termination,
            return, deletion, or other obligations in the Customer Agreement.
          </p>
          <p>Company may terminate or suspend access to the Services in accordance with these Product Terms or the Customer Agreement.</p>
          <p>
            Upon termination, Customer must stop using the Services, and Company may delete or retain Customer
            Data in accordance with the Customer Agreement, DPA, retention policies, and applicable law.
          </p>
        </section>

        <section>
          <h2>21. Export Compliance and Sanctions</h2>
          <p>Customer must comply with all applicable export control, sanctions, and trade compliance laws.</p>
          <p>
            Customer may not use, export, re-export, transfer, or provide access to the Services in violation
            of U.S. export control laws, sanctions, or other applicable trade restrictions.
          </p>
          <p>
            Customer represents that it and its Authorized Users are not located in, organized under the laws
            of, or ordinarily resident in any country or territory subject to comprehensive U.S. sanctions and
            are not listed on any restricted party list.
          </p>
        </section>

        <section>
          <h2>22. Government Use</h2>
          <p>
            The Services are commercial products. If the Services are used by or on behalf of a government
            entity, they are provided as commercial computer software and commercial technical data with only
            those rights granted under these Product Terms and the Customer Agreement.
          </p>
        </section>

        <section>
          <h2>23. Governing Law</h2>
          <p>These Product Terms are governed by the laws specified in the Customer Agreement.</p>
          <p>
            If no Customer Agreement applies, these Product Terms are governed by the laws of the State of
            California, without regard to conflict of law principles.
          </p>
        </section>

        <section>
          <h2>24. Notices</h2>
          <p>
            Company may provide notices by email, posting within the Services, including notice in
            Documentation, or other reasonable means.
          </p>
          <p>Legal notices to Company must be sent to:</p>
          <address className="terms-address">
            Bltz AI, Inc.<br />
            625 Corte Galante<br />
            San Marcos, CA 92069<br />
            Attn: Legal Department<br />
            Email: <a href="mailto:legal@bltz.ai">legal@bltz.ai</a>
          </address>
        </section>

        <section>
          <h2>25. Miscellaneous</h2>
          <p>
            Customer may not assign or transfer these Product Terms without Company's prior written consent,
            except as permitted under the Customer Agreement.
          </p>
          <p>If any provision of these Product Terms is held invalid or unenforceable, the remaining provisions will remain in full force and effect.</p>
          <p>Company's failure to enforce a provision is not a waiver.</p>
          <p>These Product Terms do not create any partnership, joint venture, agency, fiduciary, or employment relationship.</p>
          <p>There are no third-party beneficiaries to these Product Terms unless expressly stated in the Customer Agreement.</p>
          <p>Headings are for convenience only and do not affect interpretation.</p>
        </section>
      </div>
    </div>
  );
};

export default SoftwareTerms;
